// ConceptMapHeader — Compact info bar above the concept map canvas
//
// Shows: simplified Bloom level (Viden/Forståelse/Anvendelse),
// total session count, and last teaching form used.

import { useTranslation } from 'react-i18next';
import type { BloomLevel, TeachingForm } from '../../types';

interface ConceptMapHeaderProps {
  /** Aggregated Bloom level from the latest session */
  currentBloomLevel: BloomLevel;
  /** How many sessions have used this concept map */
  totalSessions: number;
  /** Which teaching form was last used */
  lastTeachingForm?: TeachingForm;
}

/** Map 6-tier Bloom to user-facing 3-tier labels */
function simplifyBloom(level: BloomLevel): 'knowledge' | 'understanding' | 'application' {
  switch (level) {
    case 'recognize':
      return 'knowledge';
    case 'understand':
      return 'understanding';
    case 'apply':
    case 'analyze':
    case 'evaluate':
    case 'create':
      return 'application';
  }
}

export default function ConceptMapHeader({
  currentBloomLevel,
  totalSessions,
  lastTeachingForm,
}: ConceptMapHeaderProps) {
  const { t } = useTranslation('session');
  const simplified = simplifyBloom(currentBloomLevel);

  return (
    <div className="flex items-center gap-4 px-4 py-2 bg-gray-900/80 border-b border-gray-800 text-xs text-gray-400">
      <span className="flex items-center gap-1.5">
        <span className="text-gray-500">{t('conceptMapHeader.level')}</span>
        <span className="text-gray-200 font-medium">
          {t(`conceptMapHeader.bloom.${simplified}`)}
        </span>
      </span>

      <span className="text-gray-700">|</span>

      <span className="flex items-center gap-1.5">
        <span className="text-gray-500">{t('conceptMapHeader.sessions')}</span>
        <span className="text-gray-200 font-medium">{totalSessions}</span>
      </span>

      {lastTeachingForm && (
        <>
          <span className="text-gray-700">|</span>
          <span className="flex items-center gap-1.5">
            <span className="text-gray-500">{t('conceptMapHeader.lastForm')}</span>
            <span className="text-gray-200 font-medium">
              {t(`conceptMapHeader.forms.${lastTeachingForm}`)}
            </span>
          </span>
        </>
      )}
    </div>
  );
}
